import { useEffect, useCallback } from 'react';

/** Detail payload emitted by ai-action-buttons via CustomEvent. */
interface ActionEventDetail {
  readonly actionId: string;
  readonly action: string;
  readonly params?: Record<string, unknown>;
}

/** Detail payload emitted by ai-form via CustomEvent. */
interface FormSubmitEventDetail {
  readonly formId: string;
  readonly values: Record<string, unknown>;
}

interface UseGenerativeUIEventsOptions {
  /** Callback invoked when the user interacts with a generative UI block. */
  readonly onUserAction: (actionMessage: string) => void;
  /** Whether the hook should be active (e.g. false when processing). */
  readonly enabled: boolean;
}

function formatActionMessage(detail: ActionEventDetail): string {
  const paramsSummary =
    detail.params && Object.keys(detail.params).length > 0
      ? ` with parameters: ${JSON.stringify(detail.params)}`
      : '';
  return `[User action] Clicked "${detail.action}"${paramsSummary}`;
}

function formatFormMessage(detail: FormSubmitEventDetail): string {
  const entries = Object.entries(detail.values)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(', ');
  return `[User form submission] ${entries}`;
}

/**
 * Listens for CustomEvents dispatched by generative UI components
 * (action buttons and forms) and feeds them back as user messages
 * so the AI assistant can respond to user interactions.
 */
export function useGenerativeUIEvents({
  onUserAction,
  enabled,
}: UseGenerativeUIEventsOptions): void {
  const handleAction = useCallback(
    (event: Event) => {
      if (!enabled) return;
      const customEvent = event as CustomEvent<ActionEventDetail>;
      const message = formatActionMessage(customEvent.detail);
      onUserAction(message);
    },
    [onUserAction, enabled],
  );

  const handleFormSubmit = useCallback(
    (event: Event) => {
      if (!enabled) return;
      const customEvent = event as CustomEvent<FormSubmitEventDetail>;
      const message = formatFormMessage(customEvent.detail);
      onUserAction(message);
    },
    [onUserAction, enabled],
  );

  useEffect(() => {
    document.addEventListener('ai-assistant:action', handleAction);
    document.addEventListener('ai-assistant:form-submit', handleFormSubmit);

    return () => {
      document.removeEventListener('ai-assistant:action', handleAction);
      document.removeEventListener('ai-assistant:form-submit', handleFormSubmit);
    };
  }, [handleAction, handleFormSubmit]);
}
